import * as z from 'zod';
import { ZodObject } from 'zod';
import { MiscellaneousField } from '../../reportsTypes';
import { isNonEmpty } from '@ais-ui/utils';

type FormData = {
  [key: string]: any;
};

function compareValues(value1: any, value2: any, operator: '<' | '>'): boolean {
  switch (operator) {
    case '>':
      return value1 > value2;
    case '<':
      return value1 < value2;
    default:
      throw new Error(`Unsupported operator: ${operator}`);
  }
}

function compareListSize(
  listSize: number,
  compareValue: number,
  operator: string
): boolean {
  switch (operator) {
    case '>':
      return listSize > compareValue;
    case '<':
      return listSize < compareValue;
    default:
      return false;
  }
}

const ColorIntervalSchema = z.object({
  color: z.string(),
  startInterval: z.number(),
  endInterval: z.number(),
});

const VideoSegmentSchema = z.object({
  dateRange: z.object({ from: z.date(), to: z.date() }),
  shapeId: z.string(),
  videoSpeed: z.number(),
  showLabels: z.boolean(),
  showAllShips: z.boolean(),
});

const SelectTypeSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export function createDynamicMiscellaneousZodSchema(
  fields: Record<string, MiscellaneousField> | undefined
): ZodObject<Record<string, z.ZodTypeAny>> {
  // email and phone are always present as it should be possible to get notified when a report has finished
  const schemaFields: Record<string, z.ZodTypeAny> = {
    email: z
      .string()
      .refine((value) => value === '' || /.+@.+\..+/.test(value), {
        message: 'Invalid email format',
      }),
    selectedGroups: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    ),
    countryCode: z
      .string()
      .refine((value) => value === '' || /^\+[0-9]{1,3}$/.test(value), {
        message: 'Invalid country code format',
      }),
    phone: z
      .string()
      .refine((value) => value === '' || /^[0-9]{1,14}$/.test(value), {
        message: 'Invalid phone number format',
      }),
  };
  let baseSchema: any = z.object(schemaFields);
  baseSchema = baseSchema.refine(
    (data: Record<string, z.ZodTypeAny>) => {
      // If countryCode is filled out, then phone also has to be filled out
      if (data.countryCode && !data.phone) {
        return false;
      }
      // If phone is filled out, then countryCode also has to be filled out
      if (data.phone && !data.countryCode) {
        return false;
      }
      return true;
    },
    {
      message: 'Both country code and phone need to be filled out',
      path: ['countryCode', 'phone'],
    }
  );

  if (!fields) {
    return baseSchema;
  }

  for (const key in fields) {
    const field = fields[key];

    switch (field.fieldType) {
      case 'date':
        if (field.required && !field.enabledOn) {
          schemaFields[key] = z.date();
        } else {
          // If the field has an enabledOn condition OR is not required
          schemaFields[key] = z.date().optional().or(z.literal(''));
        }
        break;
      case 'select':
        if (field.options && field.options.length > 0) {
          // If the field has an enabledOn condition, make it optional
          if (field.enabledOn !== undefined) {
            schemaFields[key] = z
              .enum(
                field.options.map((option) => option.value) as [
                  string,
                  ...string[]
                ]
              )
              .optional()
              .or(z.null());
          } else if (field.required) {
            schemaFields[key] = z.enum(
              field.options.map((option) => option.value) as [
                string,
                ...string[]
              ]
            );
          } else {
            schemaFields[key] = z
              .enum(
                field.options.map((option) => option.value) as [
                  string,
                  ...string[]
                ]
              )
              .optional();
          }
        } else {
          schemaFields[key] = z.string().or(z.undefined());
        }
        break;
      case 'multiSelect':
        if (field.options && field.options.length > 0) {
          // If field.enabledOn is not undefined, allow the field to be null.
          const arraySchema = z.array(SelectTypeSchema);
          schemaFields[key] =
            field.enabledOn !== undefined
              ? arraySchema.or(z.null()).optional()
              : field.required
              ? arraySchema
              : arraySchema.optional();
        } else {
          schemaFields[key] = field.required
            ? z.array(SelectTypeSchema)
            : z.array(SelectTypeSchema).optional();
        }
        break;
      case 'string':
      case 'text':
        if (field.required && !field.enabledOn) {
          schemaFields[key] = z.string();
        } else {
          schemaFields[key] = z.string().optional();
        }
        break;
      case 'dayTimeDuration':
        if (field.required && !field.enabledOn) {
          schemaFields[key] = z
            .string()
            .regex(
              /^\d+ \d{2}:\d{2}$/,
              'Invalid duration format. Expected format: "DD HH:MM"'
            );
        } else {
          schemaFields[key] = z
            .string()
            .regex(
              /^\d+ \d{2}:\d{2}$/,
              'Invalid duration format. Expected format: "DD HH:MM"'
            )
            .optional()
            .or(z.literal(''));
        }
        break;
      case 'timeDuration':
        if (field.required && !field.enabledOn) {
          schemaFields[key] = z
            .string()
            .regex(
              /^\d{2}:\d{2}$/,
              'Invalid duration format. Expected format: "HH:MM"'
            );
        } else {
          schemaFields[key] = z
            .string()
            .regex(
              /^\d{2}:\d{2}$/,
              'Invalid duration format. Expected format: "HH:MM"'
            )
            .optional()
            .or(z.literal(''));
        }
        break;

      case 'boolean':
        schemaFields[key] = z.boolean();
        break;
      case 'number':
        if (field.required && !field.enabledOn) {
          schemaFields[key] = z.number();
        } else {
          schemaFields[key] = z.number().optional().or(z.literal(''));
        }
        break;
      case 'colorInterval':
        schemaFields[key] = z.array(ColorIntervalSchema);
        break;
      case 'videoSegments':
        schemaFields[key] = z.array(VideoSegmentSchema);
        break;
    }
  }

  baseSchema = baseSchema.superRefine(
    (data: FormData, ctx: z.RefinementCtx) => {
      // For each field with enabledOn condition
      for (const key in fields) {
        const field = fields[key];

        if (field.enabledOn && field.required) {
          const { field: conditionField, value: conditionValue } =
            field.enabledOn;

          // If the condition is met (the enabledOn field has the expected value)
          // and this field is required, then validate its presence
          if (
            data[conditionField] === conditionValue &&
            (data[key] === undefined || data[key] === null || data[key] === '')
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.name} is required when ${conditionField} is ${conditionValue}`,
              path: [key],
            });
          }
        }
      }
    }
  );

  for (const key in fields) {
    const field = fields[key];
    if (field.validation) {
      const validationOperator = field.validation.operator;
      const compareValue = field.validation.compareValue;
      const compareField = field.validation.compareField;

      // Check for list size validation
      if (compareValue !== undefined) {
        baseSchema = baseSchema.refine(
          (data: FormData) => {
            const fieldValue = data[key];
            if (fieldValue === undefined || !Array.isArray(fieldValue)) {
              return true; // Field is not an array, skip this validation
            }
            return compareListSize(
              fieldValue.length,
              compareValue,
              validationOperator
            );
          },
          {
            message: field.validation.message,
            path: [key],
          }
        );
      }
      // Existing field comparison validation
      else if (compareField) {
        baseSchema = baseSchema.refine(
          (data: FormData) => {
            if (!isNonEmpty(data[key]) || !isNonEmpty(data[compareField])) {
              return true;
            }
            return compareValues(
              data[key],
              data[compareField],
              validationOperator
            );
          },
          {
            message: field.validation.message,
            path: [key],
          }
        );
      }
    }
  }

  return baseSchema;
}
